import { Wallet2Icon } from "lucide-react";
import { CustomHighChart } from "../ui/chart/custom-highchart";
import { ChartMainCard } from "./ChartsHeader";
import DataNotFound from "../ui/loader/data-not-found";
import InnerLoader from "../ui/loader/inner-loader";
import { useMemo } from "react";

const LeadsEngagement = ({ pipData = [], totalData, lineLoading }) => {
  const getShortMonth = (fullMonth) => {
    const date = new Date(fullMonth);
    if (!isNaN(date)) {
      return date.toLocaleString("en-US", { month: "short", year: "numeric" });
    }
    const monthMap = {
      January: "Jan",
      February: "Feb",
      March: "Mar",
      April: "Apr",
      May: "May",
      June: "Jun",
      July: "Jul",
      August: "Aug",
      September: "Sep",
      October: "Oct",
      November: "Nov",
      December: "Dec",
    };
    const [monthName, year] = fullMonth.split(" ");
    return `${monthMap[monthName] || monthName} ${year}`;
  };

    const filteredData = useMemo(() => {
      const firstValidIndex = pipData?.findIndex(
        (item) => item?.contacted > 0 || item?.not_contacted > 0 || item?.opportunity > 0 ,
      );
      return firstValidIndex !== -1 ? pipData?.slice(firstValidIndex) : [];
    }, [pipData]);

  const lineOptions = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      height: 300,
    },
    title: { text: "" },
    xAxis: {
      categories: filteredData?.map((d) => getShortMonth(d?.month)),
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
      labels: {
        style: {
          color: "#64748B",
          fontWeight: "500",
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "Leads Engagement Overview",
        style: { color: "#475569", fontWeight: "600" },
      },
      gridLineColor: "#F1F5F9",
      labels: {
        style: {
          color: "#64748B",
          fontWeight: "500",
        },
      },
    },
    legend: {
      align: "right",
      verticalAlign: "top",
      itemStyle: {
        color: "#334155",
        fontWeight: "500",
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 8,
        pointWidth: 35,
        states: {
          hover: {
            brightness: 0.1, // subtle interactive hover
          },
        },
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: "#0F172A",
      borderRadius: 8,
      style: {
        color: "#F8FAFC",
      },
      useHTML: true,
      formatter: function () {
        const d = filteredData[this.points[0].point.index];
        return `
        <b style="font-size:13px">${getShortMonth(d?.month)}</b><br/>
        Contacted: <b>${d?.contacted}</b><br/>
        Not Contacted: <b>${d?.not_contacted}</b><br/>
        Opportunity: <b>${d?.opportunity}</b><br/>
      `;
      },
    },
    series: [
      {
        name: "Contacted",
        data: filteredData?.map((d) => d?.contacted || 0),
        color: "#3B82F6", // Professional Blue
        stack: "Leads",
      },
      {
        name: "Not Contacted",
        data: filteredData?.map((d) => d?.not_contacted || 0),
        color: "#F59E0B", // Modern Amber
        stack: "Leads",
      },
      {
        name: "Opportunity",
        data: filteredData?.map((d) => d?.opportunity || 0),
        color: "#10B981", // Clean Emerald
        stack: "Leads",
      },
    ],
    credits: { enabled: false },
  };
  return (
    <ChartMainCard
      Icon={<Wallet2Icon />}
      iconColor={"bg-theme-orange-light text-theme-orange"}
      heading="Leads Engagement Overview"
      subHeading={
        "Breakdown of Opportunities, Contacted, and Not Contacted Leads"
      }
    >
      {!lineLoading ? (
        <div className="p-6">
          {totalData && (
            <div className="flex gap-5 mb-4 w-full">
              {/* Contacted */}
              <div className="flex-1 bg-blue-100 p-4 rounded-xl flex flex-col shadow">
                <span className="text-blue-500 text-sm font-medium">
                  Contacted
                </span>
                <span className="text-blue-900 text-2xl font-bold">
                  {totalData?.total_contacted || 0}
                </span>
              </div>

              {/* Not Contacted */}
              <div className="flex-1 bg-amber-100 p-4 rounded-xl flex flex-col shadow">
                <span className="text-amber-500 text-sm font-medium">
                  Not Contacted
                </span>
                <span className="text-amber-800 text-2xl font-bold">
                  {totalData?.total_not_contacted || 0}
                </span>
              </div>

              {/* Opportunity */}
              <div className="flex-1 bg-emerald-100 p-4 rounded-xl flex flex-col shadow">
                <span className="text-emerald-500 text-sm font-medium">
                  Opportunity
                </span>
                <span className="text-emerald-900 text-2xl font-bold">
                  {totalData?.total_opportunity || 0}
                </span>
              </div>
            </div>
          )}
          {pipData?.length > 0 ? (
            <>
              <CustomHighChart
                isLoad={!lineLoading}
                options={lineOptions}
                titleNotFound="Leads Engagement"
              />
            </>
          ) : (
            <DataNotFound />
          )}
        </div>
      ) : (
        <InnerLoader />
      )}
    </ChartMainCard>
  );
};

export default LeadsEngagement;
